/**
 * The card entry on the payer's page (Guest QR Platform, piece 3, Task 8) —
 * the PCI boundary of pay at table, and nothing else.
 *
 * `CheckPayPage` holds a signed check token and nothing else: no session, no
 * restaurant document, no `providerRouting`. It learns which gateway this
 * business uses from the ONE thing it can ask — `POST /checks/:token/card-session`
 * — and hands the answer here, so this component never decides a provider and
 * never mints a session.
 *
 * ── Why not `PaymentInput` ─────────────────────────────────────────────────
 * That wrapper's TRX branch MINTS ITS OWN session through
 * `POST /payments/trx/paypage/init`, which requires auth and a numeric
 * tenant/restaurant scope. A guest arriving cold from a QR has neither. The
 * shared `<TrxCardFields>` exists for exactly this case — its own header:
 * "The SESSION is a prop. The parent mints it against whatever endpoint its
 * surface authorizes" — and being the SHARED component is also what keeps this
 * page's card entry from drifting away from checkout's and Books'.
 *
 * ── One handle ─────────────────────────────────────────────────────────────
 * The TRX PayPage encrypted blob is extracted IMPERATIVELY at submit, and the
 * extract call DEACTIVATES the fields whatever the outcome (§11.6), which is
 * why {@link CheckCardEntryHandle.enableFields} exists and why the page calls
 * it after a decline. The gateway is discriminated on `kind`, so a second
 * processor is one more arm here.
 *
 * `extract()` returns the wire instrument the `/pay` body accepts, already
 * narrowed: the server body is `.strict()` at every level, so the TRX arm
 * carries `{ data, keyId }` and NOT the `bin` / `paymentType` the library also
 * returns — sending either is a 400, not an extra field the route ignores.
 *
 * An init error is reported through `onValidationError` and is NEVER fatal
 * here: the component stays mounted and the page keeps its Pay button. An
 * optional part of a payment form failing to mount must not take the required
 * part with it — that is the class that broke the card form for every
 * non-Safari customer once already.
 *
 * ── Wallets ────────────────────────────────────────────────────────────────
 * `enableWallets` asks the TRX arm to mount TRX's OWN Apple Pay / Google Pay
 * buttons above the card slab — the owner `resolveWalletOwner` names for TRX.
 * The page owns that choice, exactly as `CheckoutPage` does.
 *
 * `onWalletResult` hands up the wire payload `/pay` takes as `walletPayment`.
 * The page submits ON RECEIPT — the cryptogram expires in about a minute, so
 * there is no staging it behind the Pay button.
 */

import { TrxCardFields, type TrxCardFieldsHandle } from '@order-up/common';
import type {
    CheckCardSession,
    CheckPaymentInstrument,
    CheckWalletPayment,
} from '@order-up/client/vite';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

/**
 * The app's build-time env, read through a `typeof` guard.
 *
 * `__VITE_ENV__` is a `define` (vite.config.ts), so it exists in every real
 * build and in NO test process. Reading it bare at module scope makes this file
 * unimportable under vitest — and since `App.tsx` imports the page that imports
 * this, that would take down suites that never touch a card form at all (it
 * broke `posKeyedEntryRouting.test.tsx`, which mocks every page and still
 * loaded `App`). Vite replaces the identifier textually, `typeof` included, so
 * the guard costs nothing in production and removes the landmine everywhere
 * else.
 */
function viteEnv(): { TRX_PAYPAGE_FIELDS_URL?: string } {
    return typeof __VITE_ENV__ === 'undefined' ? {} : __VITE_ENV__;
}

/** The `paypage-fields` bundle. Production must carry the `.com` host. */
const TRX_SCRIPT_URL = viteEnv().TRX_PAYPAGE_FIELDS_URL;

/** Which card form to mount, as answered by the card-session route. */
export type CheckCardGateway = { readonly kind: 'trx'; readonly session: CheckCardSession };

export interface CheckCardEntryHandle {
    /**
     * The wire instrument for `POST /checks/:token/pay`, read at the SUBMIT
     * moment. Throws with a payer-readable message when the fields are not
     * ready or the card details are invalid.
     */
    extract(): Promise<CheckPaymentInstrument>;
    /** Re-activate the fields after a failed extract or a decline. */
    enableFields(): void;
}

export interface CheckCardEntryProps {
    readonly gateway: CheckCardGateway;
    /** Fires when the fields become usable (and when they stop being). */
    readonly onReady?: (ready: boolean) => void;
    /** Init / validation messages, for display. Never fatal — see the header. */
    readonly onValidationError?: (message: string) => void;
    /** Mount TRX's own Apple Pay / Google Pay buttons above the card fields. */
    readonly enableWallets?: boolean;
    /**
     * A completed wallet sheet, in the shape `/pay` takes as `walletPayment`.
     * SUBMIT IMMEDIATELY — ~1-minute cryptogram TTL.
     */
    readonly onWalletResult?: (payment: CheckWalletPayment) => void;
}

const CheckCardEntry = forwardRef<CheckCardEntryHandle, CheckCardEntryProps>(
    function CheckCardEntry(
        { gateway, onReady, onValidationError, enableWallets, onWalletResult },
        handleRef,
    ) {
        const trxRef = useRef<TrxCardFieldsHandle>(null);

        const extract = useCallback(async (): Promise<CheckPaymentInstrument> => {
            const fields = trxRef.current;
            if (gateway.kind !== 'trx' || !fields) {
                throw new Error('The card form is still loading — please try again in a moment.');
            }
            const encrypted = await fields.extractEncryptedData();
            // Named fields ONLY: the library also returns `bin`, and the
            // server's body is strict at every level, so spreading it is a 400.
            return {
                provider: 'trx',
                encrypted: { data: encrypted.data, keyId: encrypted.keyId },
            };
        }, [gateway.kind]);

        const enableFields = useCallback(() => {
            trxRef.current?.enableFields();
        }, []);

        useImperativeHandle(handleRef, () => ({ extract, enableFields }), [extract, enableFields]);

        return (
            <TrxCardFields
                ref={trxRef}
                session={gateway.session}
                {...(TRX_SCRIPT_URL ? { scriptUrl: TRX_SCRIPT_URL } : {})}
                {...(onReady ? { onReady } : {})}
                {...(onValidationError ? { onValidationError } : {})}
                {...(enableWallets ? { enableWallets } : {})}
                {...(onWalletResult ? { onWalletResult } : {})}
            />
        );
    },
);

export default CheckCardEntry;
